import { useState, useEffect, useCallback } from 'react';

export interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  consentDate: string;
}

const COOKIE_CONSENT_KEY = 'kourse_cookie_consent';

const defaultConsent: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  consentDate: '',
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
    setIsLoaded(true);
  }, []);

  const saveConsent = useCallback((newConsent: CookieConsent) => {
    const consentWithDate = {
      ...newConsent,
      necessary: true, // Always true
      consentDate: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentWithDate));
    setConsent(consentWithDate);
  }, []);

  const hasConsented = useCallback(() => {
    return consent !== null && consent.consentDate !== '';
  }, [consent]);

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      consentDate: new Date().toISOString(),
    });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      consentDate: new Date().toISOString(),
    });
  }, [saveConsent]);

  const updateConsent = useCallback((partial: Partial<Omit<CookieConsent, 'necessary' | 'consentDate'>>) => {
    const current = consent || defaultConsent;
    saveConsent({
      ...current,
      ...partial,
      necessary: true,
      consentDate: new Date().toISOString(),
    });
  }, [consent, saveConsent]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setConsent(null);
  }, []);

  return {
    consent,
    isLoaded,
    hasConsented,
    saveConsent,
    acceptAll,
    rejectAll,
    updateConsent,
    resetConsent,
  };
};
